import classNames from "classnames";
import { BaseImage } from "../../../../../../../components/Content/BaseImage.tsx";
import { ThumbnailContent } from "../../../../../../../components/Content/ThumbnailContent.tsx";
import { ClickableIcon } from "../../../../../../../components/Icon/ClickableIcon.tsx";
import { Spinner } from "../../../../../../../components/Spinner/Spinner.tsx";
import { useAppQuery } from "../../../../../../../http/useAppQuery.ts";
import { notifier } from "../../../../../../../utils/notifier.tsx";
import type { ModelGenerationParams } from "../../../../../../boardState/stores/selectedModels/types.ts";
import {
  DEFAULT_MODELS_UUIDS,
  GENERIC_STYLE_MODEL_UUID,
} from "../../../../../../constants.ts";
import type { ModelWithContents } from "../../../../../../types.ts";
import { PanelSection } from "../../../../../components/Panel/PanelSection.tsx";
import {
  BACKGROUND_REPLACE_GENERATION_TYPE,
  BRIEF_TO_IMAGE_GENERATION_TYPE,
  COPY_AD_GENERATION_TYPE,
  EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE,
  ENHANCE_GENERATION_TYPE,
  EXPAND_GENERATION_TYPE,
  GENERATIVE_FILL_GENERATION_TYPE,
  IMAGE_RESOURCE_TYPE,
  IMAGE_TO_IMAGE_GENERATION_TYPE,
  LORA_GENERATION_TYPE,
  REFRAME_GENERATION_TYPE,
  STYLE_TRANSFER_GENERATION_TYPE,
  TEXT_TO_IMAGE_GENERATION_TYPE,
  UPSCALER_GENERATION_TYPE,
} from "../../../../../constants.ts";
import { useSelectedAsset } from "../../../../../hooks/useSelectedAsset.ts";
import type { DetailedContent } from "../../../../../types.ts";
import { TextItemSectionDetails } from "../../../../components/SectionDetails.tsx";
import { GeneralInformationSection } from "./GeneralInformationSection.tsx";

export const InformationSection = () => {
  const { selectedAsset: asset } = useSelectedAsset();

  const modelsFilteredWithoutGeneric =
    asset?.content.information.generation_parameters?.generation_type ===
      TEXT_TO_IMAGE_GENERATION_TYPE ||
    asset?.content.information.generation_parameters?.generation_type ===
      BRIEF_TO_IMAGE_GENERATION_TYPE ||
    asset?.content.information.generation_parameters?.generation_type ===
      GENERATIVE_FILL_GENERATION_TYPE ||
    asset?.content.information.generation_parameters?.generation_type ===
      STYLE_TRANSFER_GENERATION_TYPE ||
    asset?.content.information.generation_parameters?.generation_type ===
      IMAGE_TO_IMAGE_GENERATION_TYPE
      ? asset.content.information.generation_parameters.references?.filter(
          (model) => model.uuid !== GENERIC_STYLE_MODEL_UUID,
        )
      : undefined;

  return (
    asset !== undefined && (
      <div className="flex-col divide-y-025">
        <GeneralInformationSection />
        <PromptInformationSection
          title="Brief"
          prompt={
            asset.content.information.generation_parameters?.generation_type ===
            BRIEF_TO_IMAGE_GENERATION_TYPE
              ? asset.content.information.generation_parameters.user_prompt
              : undefined
          }
        />
        <PromptInformationSection
          title="Prompt"
          prompt={
            asset.content.information.generation_parameters?.generation_type ===
            BRIEF_TO_IMAGE_GENERATION_TYPE
              ? asset.content.information.generation_parameters.generated_prompt
              : asset.content.information.generation_parameters
                  ?.generation_type === TEXT_TO_IMAGE_GENERATION_TYPE ||
                asset.content.information.generation_parameters
                  ?.generation_type ===
                  EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE ||
                asset.content.information.generation_parameters
                  ?.generation_type === IMAGE_TO_IMAGE_GENERATION_TYPE ||
                asset.content.information.generation_parameters
                  ?.generation_type === STYLE_TRANSFER_GENERATION_TYPE ||
                asset.content.information.generation_parameters
                  ?.generation_type === UPSCALER_GENERATION_TYPE ||
                asset.content.information.generation_parameters
                  ?.generation_type === GENERATIVE_FILL_GENERATION_TYPE ||
                asset.content.information.generation_parameters
                  ?.generation_type === EXPAND_GENERATION_TYPE
              ? asset.content.information.generation_parameters.user_prompt
              : null
          }
        />
        {modelsFilteredWithoutGeneric?.length ? (
          <PanelSection title="Models used">
            {modelsFilteredWithoutGeneric.map(
              (modelGenerationParams: ModelGenerationParams) => (
                <ModelInformationSection
                  key={modelGenerationParams.uuid}
                  modelGenerationParams={modelGenerationParams}
                />
              ),
            )}
          </PanelSection>
        ) : (
          <PanelSection title="Models used" rightItem="None" />
        )}
        <InitImageInformationSection content={asset.content} />
      </div>
    )
  );
};

const ModelInformationSection = ({
  modelGenerationParams,
}: {
  modelGenerationParams: ModelGenerationParams;
}) => {
  const { data: model, isLoading } = useAppQuery<ModelWithContents>({
    queryKey: `references/${modelGenerationParams.uuid}`,
  });

  return isLoading ? (
    <Spinner />
  ) : model ? (
    <div className="flex-col gap-150">
      <div className="grid grid-cols-4 gap-200 w-fit pb-150">
        {model.contents.slice(0, 4).map((it) => (
          <ThumbnailContent
            key={it.uuid}
            resourceType={IMAGE_RESOURCE_TYPE}
            src={it.url}
            className="w-full h-full aspect-square rounded-100 border-025 overflow-hidden"
          />
        ))}
      </div>
      {model.name && (
        <TextItemSectionDetails
          name={model.type.toLowerCase().upperFirst()}
          value={model.name}
        />
      )}
      {modelGenerationParams.model_generation_type === LORA_GENERATION_TYPE &&
        !DEFAULT_MODELS_UUIDS.includes(model.uuid) && (
          <TextItemSectionDetails
            name="Model influence"
            value={String(Math.round(modelGenerationParams.scale * 100)) + "%"}
          />
        )}
      {modelGenerationParams.model_generation_type ===
        REFRAME_GENERATION_TYPE ||
      modelGenerationParams.model_generation_type ===
        BACKGROUND_REPLACE_GENERATION_TYPE ? (
        <>
          <TextItemSectionDetails
            name="Consistency"
            value={
              modelGenerationParams.model_generation_type ===
              REFRAME_GENERATION_TYPE
                ? "LOW"
                : "HIGH"
            }
          />
          {modelGenerationParams.model_generation_type ===
            BACKGROUND_REPLACE_GENERATION_TYPE && (
            <>
              <TextItemSectionDetails
                name="Resize factor"
                value={String(modelGenerationParams.resize_factor)}
              />
              {modelGenerationParams.background_replace_quality_preset ===
                "HIGH" && (
                <>
                  <TextItemSectionDetails
                    name="Background replacement mode"
                    value={modelGenerationParams.background_replacement_mode}
                  />
                  <TextItemSectionDetails
                    name="Object rerendered quality"
                    value={
                      modelGenerationParams.object_rerendered_quality_preset
                    }
                  />
                  <TextItemSectionDetails
                    name="Is upscaled"
                    value={String(modelGenerationParams.is_upscaled)}
                  />
                </>
              )}
            </>
          )}
        </>
      ) : null}
    </div>
  ) : null;
};

const PromptInformationSection = ({
  prompt,
  title,
  variant = "light",
}: {
  title: string;
  prompt?: string | null;
  variant?: "light" | "dark";
}) =>
  prompt !== undefined &&
  prompt !== null && (
    <PanelSection
      title={title}
      rightItem={
        <ClickableIcon
          name="Copy"
          size="sm"
          variant="tertiary"
          onClick={() =>
            navigator.clipboard
              .writeText(prompt)
              .then(() => notifier.success("Prompt copied to clipboard!"))
          }
        />
      }
    >
      <div
        className={classNames(
          variant === "dark" ? "text-inverse-primary" : "text-primary",
        )}
      >
        {prompt ? prompt : "No prompt used"}
      </div>
    </PanelSection>
  );

const InitImageInformationSection = ({
  content,
}: {
  content: DetailedContent;
}) => {
  const initImageUuid =
    content.information.generation_parameters?.generation_type ===
      UPSCALER_GENERATION_TYPE ||
    content.information.generation_parameters?.generation_type ===
      GENERATIVE_FILL_GENERATION_TYPE ||
    content.information.generation_parameters?.generation_type ===
      EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE ||
    content.information.generation_parameters?.generation_type ===
      IMAGE_TO_IMAGE_GENERATION_TYPE ||
    content.information.generation_parameters?.generation_type ===
      EXPAND_GENERATION_TYPE ||
    content.information.generation_parameters?.generation_type ===
      ENHANCE_GENERATION_TYPE ||
    content.information.generation_parameters?.generation_type ===
      STYLE_TRANSFER_GENERATION_TYPE ||
    content.information.generation_parameters?.generation_type ===
      COPY_AD_GENERATION_TYPE
      ? content.information.generation_parameters.init_image_uuid
      : undefined;

  const { data: initImageData } = useAppQuery<{ url: string }>({
    queryKey: initImageUuid ? `contents/${initImageUuid}` : null,
    queryParamsKey: {
      size: "LARGE512",
    },
  });

  return (
    initImageData?.url && (
      <PanelSection title="Initial image">
        <BaseImage
          src={initImageData.url}
          className="h-[150px] w-full"
          imageClassName="h-full w-full object-contain"
          loading="lazy"
        />
      </PanelSection>
    )
  );
};
